import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, ChevronRight, ArrowRight } from 'lucide-react';
import { AlternativeProduct } from '../../product/hooks/useLensPublic';
import { Link } from 'react-router-dom';

interface RxMismatchFallbackProps {
    alternatives: AlternativeProduct[];
    onFrameOnly: () => void;
    isLoading?: boolean;
}

export const RxMismatchFallback: FC<RxMismatchFallbackProps> = ({
    alternatives,
    onFrameOnly,
    isLoading = false,
}) => {
    const { t, i18n } = useTranslation('shop');
    const isRtl = i18n.dir() === 'rtl';

    const getProductName = (product: AlternativeProduct) => {
        const t = product.translations.find(t => t.locale === i18n.language)
            || product.translations[0];
        return t?.name || '';
    };

    const formatPrice = (price: number) =>
        `${t('common.currency', 'EGP')} ${price.toLocaleString()}`;

    return (
        <div className="animate-in slide-in-from-right-8 fade-in duration-300 pb-20 md:pb-4">
            {/* Warning Banner */}
            <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
                <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                <div>
                    <h3 className="text-sm font-bold text-amber-900 mb-1">
                        {t('prescription.rxMismatch.title', "This frame doesn't support your prescription")}
                    </h3>
                    <p className="text-xs text-amber-800 leading-relaxed">
                        {t('prescription.rxMismatch.description',
                            'Your prescription values fall outside the supported range for this frame. Here are some frames that work with your Rx:'
                        )}
                    </p>
                </div>
            </div>

            {/* Alternative Products */}
            {isLoading ? (
                <div className="py-8 flex justify-center">
                    <div className="w-6 h-6 border-4 border-gray-200 border-t-primary rounded-full animate-spin" />
                </div>
            ) : alternatives.length > 0 ? (
                <div className="space-y-3 mb-6">
                    <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                        {t('prescription.rxMismatch.compatibleFrames', 'Compatible Frames for Your Rx')}
                    </h4>
                    {alternatives.map((product) => {
                        const imageUrl = product.images?.[0]?.url || '/placeholder.png';
                        const name = getProductName(product);
                        return (
                            <Link
                                key={product.id}
                                to={`/product/${product.slug}`}
                                className="flex items-center gap-3 p-3 bg-white border border-gray-200 rounded-xl hover:border-primary hover:shadow-sm transition-all group"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                {/* Product Image */}
                                <div className="w-16 h-16 flex-shrink-0 bg-gray-50 rounded-lg overflow-hidden border border-gray-100">
                                    <img
                                        src={imageUrl}
                                        alt={name}
                                        className="w-full h-full object-contain"
                                    />
                                </div>

                                {/* Info */}
                                <div className="flex-1 min-w-0">
                                    <p className="font-bold text-gray-900 text-sm truncate">{name}</p>
                                    {product.gender && (
                                        <p className="text-xs text-gray-400 mt-0.5 capitalize">
                                            {t(`filters.gender.${product.gender}`, product.gender)}
                                            {product.frameShape && ` · ${product.frameShape}`}
                                        </p>
                                    )}
                                    <div className="flex items-center gap-2 mt-1">
                                        <span className="text-sm font-bold text-primary">{formatPrice(product.price)}</span>
                                        {product.compareAtPrice && product.compareAtPrice > product.price && (
                                            <span className="text-xs text-gray-400 line-through">{formatPrice(product.compareAtPrice)}</span>
                                        )}
                                    </div>
                                </div>

                                {/* Arrow */}
                                <ArrowRight
                                    className={`w-4 h-4 text-gray-400 group-hover:text-primary transition-colors flex-shrink-0 ${isRtl ? 'rotate-180' : ''}`}
                                />
                            </Link>
                        );
                    })}
                </div>
            ) : (
                <div className="text-center py-8 mb-6">
                    <p className="text-sm text-gray-500">
                        {t('prescription.rxMismatch.noAlternatives',
                            'No compatible frames found. Please contact our support team.'
                        )}
                    </p>
                </div>
            )}

            {/* Divider */}
            <div className="relative flex items-center my-4">
                <div className="flex-1 border-t border-gray-200" />
                <span className="mx-3 text-xs text-gray-400 font-medium uppercase tracking-wider">
                    {t('prescription.or', 'or')}
                </span>
                <div className="flex-1 border-t border-gray-200" />
            </div>

            {/* Frame Only Option */}
            <button
                onClick={onFrameOnly}
                className={`w-full flex items-center justify-between px-4 py-3 rounded-xl border-2 border-gray-200 bg-white hover:border-gray-400 transition-colors text-sm font-semibold text-gray-700`}
            >
                <span>{t('prescription.rxMismatch.frameOnly', 'Continue as Frame Only (no lenses)')}</span>
                <ChevronRight className={`w-4 h-4 ${isRtl ? 'rotate-180' : ''}`} />
            </button>
        </div>
    );
};
